import {listMarketBusinesses} from "./data/marketplaceBusinesses";
import AuthRecoveryNotice from "./components/AuthRecoveryNotice";
import UpdateRequiredDialog from "./components/UpdateRequiredDialog";
import {buildLoopRouteFromExternalUrl, repairLegacyRatingLink} from "./utils/loopAppLinks";
// LOOP WORKER INVITE DEEPLINK V87 â€” native app opens production worker invite links into /worker-invite.
// src/App.js
//
// iOS fix (Capacitor WKWebView):
// - Firebase Web Auth / Firestore Web SDK can fail or time out on iOS.
// - We treat "__MANUAL_AUTH__" (persisted by Login) as the effective session on iOS.
// - We load userInfo + favorites via Firestore REST (firestoreClient) when running on iOS.
// - Logout clears "__MANUAL_AUTH__" so there is no "ghost login".
// - ProtectedRoute is updated separately to accept manual-auth on iOS.
//
// This keeps Android/Web behavior unchanged as much as possible.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { BrowserRouter, useNavigate, Routes, Route, useParams, useLocation, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import useSwipeBackGesture from "./hooks/useSwipeBackGesture";
import { App as CapacitorApp } from "@capacitor/app";

import { auth, db } from "./firebase";
import useFavoriteActions from "./hooks/useFavoriteActions";
import MainMenu from "./MainMenu";
import Login from "./Login";
import Register from "./Register";
import BusinessEditRoute from "./components/BusinessEditRoute";
import WorkerBusinessesModal from "./components/WorkerBusinessesModal";
import LegalUpdateModal from "./components/LegalUpdateModal";

// âœ… Password reset pages
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";

// Pages / Components
import BusinessPage from "./pages/BusinessPage";
import CalendarPage from "./pages/CalendarPage";
import ProfilePage from "./pages/ProfilePage";
import CompleteProfileOnboarding from "./pages/CompleteProfileOnboarding";
import CustomersPage from "./pages/CustomersPage";
import LoyaltyCheckinPage from "./pages/LoyaltyCheckinPage";
import MyLoyaltyPage from "./pages/MyLoyaltyPage";
import MyMembershipsPage from "./pages/MyMembershipsPage";
import MembershipSubscriptionCheckoutPage from "./pages/MembershipSubscriptionCheckoutPage";
import MyBenefitsPage from "./pages/MyBenefitsPage";
import OwnerPage from "./pages/OwnerPage";
import ServiceResultsPage from "./pages/ServiceResultsPage";
import SupportPage from "./pages/SupportPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import PaymentsPolicyPage from "./pages/PaymentsPolicyPage";
import EventsHomePage from "./pages/EventsHomePage";
import EventCategoryPage from "./pages/EventCategoryPage";
import BusinessAnalyticsPage from "./pages/BusinessAnalyticsPage";
import EarlyAccessLeadPage from "./pages/EarlyAccessLeadPage";
import EarlyAccessAdminPage from "./pages/EarlyAccessAdminPage";
import OwnerConsolePage from "./pages/OwnerConsolePage";
import WorkerInvitePage from "./pages/WorkerInvitePage";

// âœ… Make sure this file exists:
import LocationResultsPage from "./pages/LocationResultsPage";

import MyBookings from "./MyBookings";
import NewBusinessPage from "./NewBusinessPage";
import BookingInterface from "./BookingInterface";

// Admin booking calendar
import AdminBookingCalendar from "./AdminBookingCalendar";

// IMPORTANT: import the admin PendingReservationsRoute that renders the admin panel (PendingsPanel)
import PendingReservationsRoute from "./PendingReservationsRoute";


// Pending bookings counts

// Approvals panel
import ApprovalsPanel from "./ApprovalsPanel";

// Debugger component
import MyCalendarDebugger from "./components/MyCalendarDebugger";

// Rating panel
import CalificarServicios from "./CalificarServicios";

import ProtectedRoute from "./components/ProtectedRoute";

// Login modal provider

import VerifyEmailPage from "./pages/VerifyEmail";
import EnterCode from "./EnterCode";

import fsClient from "./data/firestoreClient";
import { isIosCapacitor, httpRequestJson } from "./utils/nativeHttp";
import { getManualAuth, clearManualAuth, clearLoopAuthState, isManualAuthLikelyValid, getManualAuthStatus, persistManualAuth, shouldAllowNativeSessionRestore, getEffectiveManualAuthSession } from "./utils/manualAuth";
import { buildEffectiveSession, getEffectiveSessionKey, isEffectiveSessionLoggedIn } from "./auth/effectiveSession";
import { getCurrentAuthIdToken, getProfileCompletionCache, setProfileCompletionCache } from "./auth/socialAuth";
import { getSafeAuthRedirectPath } from "./auth/authNavigation";
import { resolveBusinessIdentifier, getBusinessRouteParam, businessAdminPath } from "./utils/businessSlug";
import { getCurrentUserSession, refreshSession, isFirebaseNativeAvailable, signOut as nativeFirebaseSignOut } from "./native/firebaseNative";
import { registerPushNotifications, unregisterPushNotifications } from "./notifications/registerPushNotifications";
import BookingNotificationToasts from "./components/BookingNotificationToasts";
import AuthDebugPanel from "./components/AuthDebugPanel";
import { buildLoopNotificationRoute, getLoopNotificationData } from "./notifications/notificationRouting";

const defaultBusinessTypes = [
  { id: "belleza", name: "Belleza & Barberias" },
  { id: "bienestar", name: "Bienestar & Spa" },
  { id: "eventos", name: "Eventos" },
  { id: "fitness", name: "Entretenimiento & Fitness" },
  { id: "otros", name: "Otros" },
];

const USER_SEARCH_LOCATION_KEY = "loop_search_location_v1";

function isLoopNativeRuntime() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap) return false;
    const platform = typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || "web";
    return platform === "ios" || platform === "android";
  } catch (_) {
    return false;
  }
}


// LOOP CALENDAR SELECTOR NAME GUARD V61
function looksLikeLoopBusinessDocId(value = "") {
  const text = String(value || "").trim();
  return /^[A-Za-z0-9_-]{16,}$/.test(text) && !/[\sÃ±Ã‘Ã¡Ã©Ã­Ã³ÃºÃÃ‰ÃÃ“Ãš#&.]/.test(text);
}

function cleanLoopBusinessDisplayName(value, businessId = "") {
  const text = String(value || "").trim();
  const id = String(businessId || "").trim();
  if (!text) return "";
  if (id && text.toLowerCase() === id.toLowerCase()) return "";
  if (looksLikeLoopBusinessDocId(text)) return "";
  return text;
}

function pickLoopBusinessDisplayName(item = {}, businessId = "") {
  const candidates = [
    item?.businessName,
    item?.name,
    item?.title,
    item?.displayName,
    item?.publicName,
    item?.business?.name,
    item?.business?.businessName,
    item?.raw?.businessName,
    item?.raw?.name,
    item?.raw?.title,
  ];
  for (const value of candidates) {
    const clean = cleanLoopBusinessDisplayName(value, businessId);
    if (clean) return clean;
  }
  return "";
}




function readSearchLocation() {
  try {
    const raw = localStorage.getItem(USER_SEARCH_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}


function isPublicBusiness(biz) {
  if (!biz || typeof biz !== "object") return false;
  return (
    biz.visible !== false &&
    biz.hidden !== true &&
    biz.isActive !== false &&
    biz.published !== false
  );
}

function writeSearchLocation(valueOrNull) {
  try {
    if (!valueOrNull) localStorage.removeItem(USER_SEARCH_LOCATION_KEY);
    else localStorage.setItem(USER_SEARCH_LOCATION_KEY, JSON.stringify(valueOrNull));
  } catch {}
}

function isManualAuthUsableNow() {
  try {
    const ma = getManualAuth();
    return Boolean(ma && isManualAuthLikelyValid(ma));
  } catch (_) {
    return false;
  }
}

function isPasswordProviderUser(user) {
  try {
    return Array.isArray(user?.providerData) && user.providerData.some((p) => p?.providerId === "password");
  } catch (_) {
    return false;
  }
}

function isEmailVerificationAllowedPath(pathname) {
  const p = String(pathname || "");
  return (
    p === "/enter-code" ||
    p === "/register" ||
    p === "/login" ||
    p === "/forgot-password" ||
    p === "/reset-password" ||
    p === "/signup" ||
    p === "/verify-email"
  );
}

async function refreshNativeIosSessionIfPossible() {
  try {
    if (!isIosCapacitor() || !isFirebaseNativeAvailable()) return null;

    // Hard guard against iOS ghost sessions:
    // do not resurrect a native Firebase session unless this WebView previously
    // created a trusted manual session. Logout clears this marker.
    if (!shouldAllowNativeSessionRestore()) return null;

    try {
      const refreshed = await refreshSession();
      if (refreshed && refreshed.idToken && refreshed.uid) {
        const next = {
          idToken: String(refreshed.idToken || ""),
          refreshToken: null,
          expiresIn: "3600",
          uid: refreshed.uid || null,
          obtainedAt: new Date().toISOString(),
          source: refreshed.provider === "google.com" ? "ios-native-google" : "ios-native-email",
          email: refreshed.email || "",
          displayName: refreshed.displayName || "",
        };
        persistManualAuth(next);
        return next;
      }
    } catch (_) {}

    try {
      const current = await getCurrentUserSession();
      if (current && current.idToken && current.uid) {
        const next = {
          idToken: String(current.idToken || ""),
          refreshToken: null,
          expiresIn: "3600",
          uid: current.uid || null,
          obtainedAt: new Date().toISOString(),
          source: current.provider === "google.com" ? "ios-native-google" : "ios-native-email",
          email: current.email || "",
          displayName: current.displayName || "",
        };
        persistManualAuth(next);
        return next;
      }
    } catch (_) {}

    return null;
  } catch (_) {
    return null;
  }
}




/* ---------------------------
   LOOP VERSION GATE / FORCE UPDATE READY V1
   Disabled by default. It only blocks when Firestore appConfig/versionGate says so.
--------------------------- */

const LOOP_VERSION_GATE_DOC_PATH = ["appConfig", "versionGate"];

function normalizeLoopPlatform() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const platform = cap
      ? (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || "web")
      : "web";
    return platform === "android" || platform === "ios" ? platform : "web";
  } catch (_) {
    return "web";
  }
}

function parseLoopBuildNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const direct = Number.parseInt(raw, 10);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const digits = raw.match(/\d+/g);
  if (!digits || !digits.length) return 0;
  const joined = digits.join("");
  const parsed = Number.parseInt(joined, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function readLoopVersionGateField(config, platform, keys = []) {
  const platformConfig = config?.[platform] && typeof config[platform] === "object" ? config[platform] : {};
  for (const key of keys) {
    if (platformConfig[key] !== undefined && platformConfig[key] !== null) return platformConfig[key];

    const prefixedKey = `${platform}${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    if (config?.[prefixedKey] !== undefined && config?.[prefixedKey] !== null) return config[prefixedKey];
  }
  return undefined;
}

function buildLoopVersionGateDecision(config, appInfo, platform) {
  const safePlatform = platform === "android" || platform === "ios" ? platform : "web";
  const currentBuild = parseLoopBuildNumber(appInfo?.build);
  const currentVersion = String(appInfo?.version || "").trim();

  const enabled = Boolean(config?.enabled);
  if (!enabled || safePlatform === "web") {
    return {
      checked: true,
      status: "ready",
      platform: safePlatform,
      currentBuild,
      currentVersion,
      reason: enabled ? "web-not-gated" : "disabled",
    };
  }

  const minBuild = parseLoopBuildNumber(readLoopVersionGateField(config, safePlatform, ["minBuild", "minimumBuild", "minVersionCode"]));
  const latestBuild = parseLoopBuildNumber(readLoopVersionGateField(config, safePlatform, ["latestBuild", "latestVersionCode"]));
  const storeUrl = String(readLoopVersionGateField(config, safePlatform, ["storeUrl", "url", "updateUrl"]) || "");
  const title = String(config?.forceTitle || config?.title || "ActualizaciÃ³n requerida");
  const forceMessage = String(config?.forceMessage || config?.message || "Tenemos una nueva versiÃ³n de Loop. Actualiza para seguir usando la app.");
  const softMessage = String(config?.softMessage || "Hay una nueva versiÃ³n disponible con mejoras importantes.");
  const maintenanceMode = Boolean(config?.maintenanceMode);
  const allowOptionalDismiss = config?.allowOptionalDismiss !== false;
  const softUpdateEnabled = Boolean(config?.softUpdateEnabled);
  const platformForce = readLoopVersionGateField(config, safePlatform, ["forceUpdate"]);
  const forceUpdate = platformForce === undefined ? Boolean(config?.forceUpdate) : Boolean(platformForce);

  if (maintenanceMode) {
    return {
      checked: true,
      status: "required",
      platform: safePlatform,
      currentBuild,
      currentVersion,
      minBuild,
      latestBuild,
      storeUrl,
      title: String(config?.maintenanceTitle || "Loop estÃ¡ en mantenimiento"),
      message: String(config?.maintenanceMessage || "Estamos haciendo mejoras. Intenta actualizar o vuelve a abrir la app en unos minutos."),
      reason: "maintenance",
    };
  }

  if (minBuild > 0 && currentBuild > 0 && currentBuild < minBuild) {
    return {
      checked: true,
      status: "required",
      platform: safePlatform,
      currentBuild,
      currentVersion,
      minBuild,
      latestBuild,
      storeUrl,
      title,
      message: forceMessage,
      reason: "below-min-build",
    };
  }

  if (forceUpdate && latestBuild > 0 && currentBuild > 0 && currentBuild < latestBuild) {
    return {
      checked: true,
      status: "required",
      platform: safePlatform,
      currentBuild,
      currentVersion,
      minBuild,
      latestBuild,
      storeUrl,
      title,
      message: forceMessage,
      reason: "force-latest-build",
    };
  }

  if (softUpdateEnabled && latestBuild > 0 && currentBuild > 0 && currentBuild < latestBuild) {
    return {
      checked: true,
      status: "optional",
      platform: safePlatform,
      currentBuild,
      currentVersion,
      minBuild,
      latestBuild,
      storeUrl,
      title: String(config?.softTitle || "Nueva versiÃ³n disponible"),
      message: softMessage,
      allowOptionalDismiss,
      reason: "below-latest-build",
    };
  }

  return {
    checked: true,
    status: "ready",
    platform: safePlatform,
    currentBuild,
    currentVersion,
    minBuild,
    latestBuild,
    storeUrl,
    reason: "allowed",
  };
}

async function loadLoopVersionGateDecision(db) {
  const platform = normalizeLoopPlatform();
  if (platform !== "android" && platform !== "ios") {
    return { checked: true, status: "ready", platform, reason: "web" };
  }

  let appInfo = {};
  try {
    if (typeof CapacitorApp?.getInfo === "function") {
      appInfo = await CapacitorApp.getInfo();
    }
  } catch (_) {
    appInfo = {};
  }

  try {
    const ref = doc(db, ...LOOP_VERSION_GATE_DOC_PATH);
    const snap = await getDoc(ref);
    const config = snap && snap.exists() ? snap.data() || {} : { enabled: false };
    return buildLoopVersionGateDecision(config, appInfo, platform);
  } catch (error) {
    try {
      console.warn("[Loop VersionGate] config fetch failed; allowing app for now", error);
    } catch (_) {}
    return {
      checked: true,
      status: "ready",
      platform,
      currentBuild: parseLoopBuildNumber(appInfo?.build),
      currentVersion: String(appInfo?.version || ""),
      reason: "fetch-failed-allow",
    };
  }
}

function openLoopStoreUrl(url) {
  const clean = String(url || "").trim();
  if (!clean) return;
  try {
    window.location.href = clean;
  } catch (_) {
    try { window.open(clean, "_system"); } catch (__) {}
  }
}

function LoopUpdateRequiredScreen({ decision }) {
  return <UpdateRequiredDialog decision={decision} onUpdate={() => openLoopStoreUrl(decision?.storeUrl)} />;
}

function LoopSoftUpdateNotice({ decision, onDismiss }) {
  if (!decision || decision.status !== "optional") return null;

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 2147482000,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      padding: "18px",
      background: "rgba(15,23,42,.28)",
      boxSizing: "border-box",
    }}>
      <div style={{
        width: "100%",
        maxWidth: 440,
        borderRadius: 26,
        background: "#fff8ee",
        color: "#0f172a",
        padding: 18,
        boxShadow: "0 24px 70px rgba(15,23,42,.28)",
      }}>
        <div style={{ fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase", color: "#00675F", fontWeight: 950 }}>
          ActualizaciÃ³n
        </div>
        <h2 style={{ margin: "6px 0 6px", fontSize: 22, letterSpacing: "-.03em", lineHeight: 1.05 }}>
          {decision.title || "Nueva versiÃ³n disponible"}
        </h2>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45, color: "#64748b", fontWeight: 700 }}>
          {decision.message || "Hay una nueva versiÃ³n de Loop disponible."}
        </p>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          {decision.allowOptionalDismiss !== false ? (
            <button
              type="button"
              onClick={onDismiss}
              style={{
                flex: "0 0 112px",
                minHeight: 46,
                borderRadius: 16,
                border: "1px solid #dbe5e1",
                background: "#ffffff",
                color: "#334155",
                fontWeight: 900,
              }}
            >
              Luego
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => openLoopStoreUrl(decision.storeUrl)}
            style={{
              flex: 1,
              minHeight: 46,
              borderRadius: 16,
              border: 0,
              background: "#00675F",
              color: "#ffffff",
              fontWeight: 950,
            }}
          >
            Actualizar
          </button>
        </div>
      </div>
    </div>
  );
}



/* ---------------------------
   ROUTE COMPONENTS (moved outside AppInner)
--------------------------- */



function AdminBookingCalendarRouteWrapper({ renderMainMenu, db, user, authSessionKey = "guest" }) {
  const params = useParams();
  const paramId = params?.businessId || null;

  return <CalendarSafeRoute renderMainMenu={renderMainMenu} businessIdFallback={paramId} authSessionKey={authSessionKey} />;
}

function OpenCalendarRoute({ renderMainMenu, setBusinessId, setBusinessName }) {
  const navigate = useNavigate();
  const calendarSelectedRef = useRef(false);

  const handleSelect = (item) => {
    try {
      const bizId = item?.businessId || item?.id;
      if (!bizId) return;

      calendarSelectedRef.current = true;

      const safeBusinessName = pickLoopBusinessDisplayName(item, bizId);

      setBusinessId(bizId);
      setBusinessName(safeBusinessName || null);

      const calendarState = {
        businessId: bizId,
        businessName: safeBusinessName || "Cargando negocio...",
        role: item?.role || "owner",
        employeeId: item?.employeeId || null,
        employeeEmail: item?.employeeEmail || null,
        skipResolve: true,
        fromCalendarSelector: true,
      };

      if (item?.action === "analytics") {
        navigate(`/business/${encodeURIComponent(bizId)}/analytics`, { replace: true, state: calendarState });
        return;
      }

      if (item?.role === "worker" && item?.employeeId) {
        navigate(`/business/${encodeURIComponent(bizId)}/calendar?worker=${encodeURIComponent(item.employeeId)}`, { replace: true, state: calendarState });
        return;
      }

      if (item?.role === "worker" && item?.employeeEmail) {
        navigate(`/business/${encodeURIComponent(bizId)}/calendar?workerEmail=${encodeURIComponent(item.employeeEmail)}`, { replace: true, state: calendarState });
        return;
      }

      navigate(`/business/${encodeURIComponent(bizId)}/calendar`, { replace: true, state: calendarState });
    } catch (e) {
      console.warn("navigate to business calendar failed", e);
    }
  };

  const handleClose = () => {
    if (!calendarSelectedRef.current) navigate(-1);
  };

  return (
    <div>
      {renderMainMenu({ headerOnly: true })}
      <WorkerBusinessesModal
        open={true}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </div>
  );
}


class CalendarErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error && error.message ? String(error.message) : "Error cargando calendario",
    };
  }

  componentDidCatch(error, info) {
    try {
      console.error("[CalendarErrorBoundary]", error, info);
    } catch (_) {}
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        background: "#f8fafc",
      }}>
        <div style={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 24,
          border: "1px solid #e2e8f0",
          background: "#ffffff",
          boxShadow: "0 18px 48px rgba(15,23,42,0.10)",
          padding: 20,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 13, fontWeight: 950, color: "#00897f", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Calendario
          </div>
          <h2 style={{ margin: "8px 0 8px", fontSize: 24, lineHeight: 1.05, color: "#0f172a", fontWeight: 950 }}>
            No pudimos cargar el calendario
          </h2>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14, lineHeight: 1.5, fontWeight: 650 }}>
            Intenta volver atrÃ¡s y abrirlo nuevamente. Si estÃ¡s en iPhone, esto evita que la app quede en pantalla blanca.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16,
              minHeight: 46,
              borderRadius: 16,
              border: 0,
              background: "#00897f",
              color: "#ffffff",
              padding: "0 18px",
              fontWeight: 900,
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }
}


function isEventsServiceKey(value) {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

  return (
    normalized === "eventos" ||
    normalized === "events" ||
    normalized === "evento" ||
    normalized.includes("evento")
  );
}

function ServiceResultsSafeRoute({ renderMainMenu }) {
  const params = useParams();
  const serviceKey = params?.serviceKey || "";

  if (isEventsServiceKey(serviceKey)) {
    return <Navigate to="/events" replace />;
  }

  return (
    <div>
      {renderMainMenu({ headerOnly: true })}
      <ServiceResultsPage />
    </div>
  );
}


function cleanupLoopCalendarGlobalLocks() {
  if (typeof document === "undefined") return;

  const clearProps = [
    "height",
    "min-height",
    "max-height",
    "overflow",
    "overscroll-behavior",
    "margin",
    "padding-top",
  ];

  [
    document.documentElement,
    document.body,
    document.getElementById("root"),
    document.querySelector(".App"),
  ].forEach((el) => {
    if (!el || !el.style) return;
    clearProps.forEach((prop) => {
      try { el.style.removeProperty(prop); } catch (_) {}
    });
  });
}

function CalendarSafeRoute({ renderMainMenu, businessIdFallback = null, authSessionKey = "guest" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const paramBusinessId = params?.businessId || null;
  const businessParam = paramBusinessId || businessIdFallback || null;
  const stateBusinessId = location?.state?.businessId ? String(location.state.businessId) : null;
  const shouldSkipResolve = Boolean(location?.state?.skipResolve && stateBusinessId);
  const initialBusinessId = stateBusinessId || (businessParam && !String(businessParam).includes("/") ? businessParam : null);
  const [effectiveBusinessId, setEffectiveBusinessId] = useState(initialBusinessId);
  const [resolving, setResolving] = useState(Boolean(businessParam && !shouldSkipResolve && !initialBusinessId));

  // LOOP CALENDAR ROUTE LOCAL VIEWPORT V37
  // The calendar owns only its route wrapper, not html/body/#root/.App.
  // This clears stale global inline locks left by older agenda builds when
  // entering and leaving the calendar route, so MainMenu/other pages keep scrolling.
  useEffect(() => {
    cleanupLoopCalendarGlobalLocks();
    return () => cleanupLoopCalendarGlobalLocks();
  }, []);

  useEffect(() => {
    let mounted = true;
    async function resolve() {
      if (!businessParam) {
        if (mounted) {
          setEffectiveBusinessId(null);
          setResolving(false);
        }
        return;
      }

      if (shouldSkipResolve && stateBusinessId) {
        if (mounted) {
          setEffectiveBusinessId(stateBusinessId);
          setResolving(false);
        }
        return;
      }

      // Fast path: when the route param is already the Firestore business id,
      // mount the calendar immediately and resolve/canonicalize only in the background.
      if (initialBusinessId && mounted) {
        setEffectiveBusinessId(initialBusinessId);
        setResolving(false);
      } else if (mounted) {
        setResolving(true);
      }
      try {
        const result = await resolveBusinessIdentifier(db, businessParam);
        const realId = result.businessId || businessParam;
        if (mounted) setEffectiveBusinessId(realId);

        const routeParam = result.routeParam || getBusinessRouteParam(result.business || { id: realId });
        const isLegacyCalendarPath = String(location.pathname || "").startsWith("/admin-calendar");
        const shouldCanonicalize = routeParam && (isLegacyCalendarPath || String(routeParam) !== String(paramBusinessId || ""));
        if (shouldCanonicalize) {
          navigate(`/business/${encodeURIComponent(routeParam)}/calendar${location.search || ""}`, { replace: true });
        }
      } catch (_) {
        if (mounted) setEffectiveBusinessId(businessParam);
      } finally {
        if (mounted) setResolving(false);
      }
    }
    resolve();
    return () => { mounted = false; };
  }, [businessParam, paramBusinessId, navigate, location.pathname, location.search, shouldSkipResolve, stateBusinessId, initialBusinessId]);

  return (
    <div className="calendar-route-shell">
      {renderMainMenu({ headerOnly: true })}
      <div className="calendar-route-viewport" data-loop-calendar-route="true">
        <CalendarErrorBoundary>
          {resolving ? null : (
            <AdminBookingCalendar
              key={`calendar-${authSessionKey}-${effectiveBusinessId || "none"}`}
              businessId={effectiveBusinessId}
            />
          )}
        </CalendarErrorBoundary>
      </div>
    </div>
  );
}



const LOOP_AUTH_PROFILE_RESOLVE_URL_R31 =
  "https://us-central1-web-app-d9799.cloudfunctions.net/resolveAuthProfileHttpR31";

const LOOP_AUTH_PROFILE_GATE_ALLOWED_PATHS_R31 = new Set([
  "/login",
  "/register",
  "/signup",
  "/complete-profile",
  "/enter-code",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/terms",
  "/terminos",
  "/terminos-y-condiciones",
  "/privacy",
  "/privacy-policy",
  "/privacidad",
  "/politica",
  "/politica-de-privacidad",
  "/payments",
  "/payments-policy",
  "/pagos",
  "/politica-de-pagos",
  "/pagos-cancelaciones-reembolsos",
  "/support",
  "/soporte",
]);

const LOOP_STARTUP_SURFACE_COLOR = "#fff8ee";

function LoopStartupSurface({
  ariaLabel = "Inicializando Loop",
  fixed = false,
}) {
  return (
    <div
      aria-label={ariaLabel}
      style={{
        position: fixed ? "fixed" : "relative",
        inset: fixed ? 0 : undefined,
        zIndex: fixed ? 2147483000 : undefined,
        width: "100%",
        minHeight: "100vh",
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        background: LOOP_STARTUP_SURFACE_COLOR,
      }}
    >
      <img
        src="/loop-startup-mark.png"
        alt=""
        aria-hidden="true"
        draggable={false}
        style={{
          width: 128,
          maxWidth: "34vw",
          height: "auto",
          display: "block",
        }}
      />
    </div>
  );
}

function LoopAuthenticatedProfileGateR31({
  isLoggedIn = false,
  user = null,
  authSessionKey = "",
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = String(location.pathname || "/");
  const search = String(location.search || "");
  const allowed = LOOP_AUTH_PROFILE_GATE_ALLOWED_PATHS_R31.has(path);
  const manualSession = getEffectiveManualAuthSession();
  const uid = String(
    user?.uid ||
      auth?.currentUser?.uid ||
      manualSession?.user?.uid ||
      "",
  ).trim();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (!isLoggedIn || allowed || !uid) {
      setChecking(false);
      return () => {
        mounted = false;
      };
    }

    const currentTarget = getSafeAuthRedirectPath(`${path}${search}`, "/");
    const cached = getProfileCompletionCache(uid);

    if (cached === true) {
      setChecking(false);
      return () => {
        mounted = false;
      };
    }

    if (cached === false) {
      navigate(
        `/complete-profile?next=${encodeURIComponent(currentTarget)}`,
        { replace: true, state: { from: currentTarget } },
      );
      return () => {
        mounted = false;
      };
    }

    setChecking(true);

    (async () => {
      try {
        const idToken = await getCurrentAuthIdToken();
        const response = await httpRequestJson({
          url: LOOP_AUTH_PROFILE_RESOLVE_URL_R31,
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          data: { source: "global_profile_gate_r31" },
        });

        const complete = response?.profileComplete === true;
        setProfileCompletionCache(uid, complete);

        if (!complete && mounted) {
          navigate(
            `/complete-profile?next=${encodeURIComponent(currentTarget)}`,
            { replace: true, state: { from: currentTarget } },
          );
        }
      } catch (error) {
        console.warn("[ProfileGate R31] profile check failed", error);
        // This gate validates data quality; authorization remains protected
        // separately. Do not lock an authenticated user out during an outage.
      } finally {
        if (mounted) setChecking(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    allowed,
    authSessionKey,
    isLoggedIn,
    navigate,
    path,
    search,
    uid,
  ]);

  if (!checking || allowed || !isLoggedIn) return null;

  return (
    <LoopStartupSurface
      fixed
      ariaLabel="Verificando tu perfil"
    />
  );
}

/* ---------------------------
   APP ROOT
--------------------------- */

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppInner />
    </BrowserRouter>
  );
}

function LegacyRatingLinkFallback({ children }) {
  const location = useLocation();
  const corrected = repairLegacyRatingLink(location.pathname, location.search);
  return corrected ? <Navigate to={corrected + location.hash} replace /> : children;
}

function AppInner() {
  const navigate = useNavigate();
  const handleSwipeBack = useCallback(() => navigate(-1), [navigate]);

  useSwipeBackGesture({
    enabled: true,
    edgeWidth: 28,
    threshold: 86,
    maxVerticalTravel: 72,
    onBack: handleSwipeBack,
  });

  // LOOP iOS STATUS BAR / SAFE AREA SINGLE SURFACE V30
  // iOS was rendering a native status-bar strip above the web header.
  // We mark the native platform and ask Capacitor to overlay the status bar
  // so the same Loop header background fills the whole top area.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const cap = window.Capacitor || null;
    const platform = cap
      ? (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || "web")
      : "web";

    const isNative = platform === "ios" || platform === "android";
    const root = document.documentElement;
    const body = document.body;

    if (isNative) {
      root.classList.add("loop-native-runtime");
      body?.classList?.add("loop-native-runtime", "is-mobile-app");
    }

    if (platform === "ios") {
      root.classList.add("loop-ios-native");
      body?.classList?.add("loop-ios-native");

      const statusBar = cap?.Plugins?.StatusBar || window.StatusBar;
      if (statusBar) {
        Promise.resolve()
          .then(() => statusBar.setOverlaysWebView?.({ overlay: true }))
          .then(() => statusBar.setStyle?.({ style: "DARK" }))
          .then(() => statusBar.show?.())
          .catch((error) => {
            try { console.warn("[Loop iOS StatusBar] setup skipped", error); } catch (_) {}
          });
      }
    }

    if (platform === "android") {
      root.classList.add("loop-android-native");
      body?.classList?.add("loop-android-native");
    }

    try {
      window.__LOOP_NATIVE_PLATFORM__ = {
        platform,
        iosSingleSurface: platform === "ios",
        at: new Date().toISOString(),
      };
    } catch (_) {}

    return undefined;
  }, []);

  const openLoopDeepLink = useCallback((rawUrl, source = "native-url") => {
    const route = buildLoopRouteFromExternalUrl(rawUrl);
    if (!route) return false;

    try {
      if (typeof window !== "undefined") {
        window.__LOOP_LAST_DEEP_LINK__ = {
          url: rawUrl,
          route,
          source,
          at: new Date().toISOString(),
        };
      }
    } catch (_) {}

    navigate(route, { replace: false });
    return true;
  }, [navigate]);

  useEffect(() => {
    if (!isLoopNativeRuntime()) return undefined;

    let cancelled = false;
    let listenerHandle = null;

    const setupDeepLinks = async () => {
      try {
        if (typeof CapacitorApp?.getLaunchUrl === "function") {
          const launch = await CapacitorApp.getLaunchUrl();
          if (!cancelled && launch?.url) {
            openLoopDeepLink(launch.url, "getLaunchUrl");
          }
        }
      } catch (err) {
        try { console.warn("[Loop Deep Links] getLaunchUrl skipped", err); } catch (_) {}
      }

      try {
        if (typeof CapacitorApp?.addListener === "function") {
          listenerHandle = await CapacitorApp.addListener("appUrlOpen", (event) => {
            openLoopDeepLink(event?.url, "appUrlOpen");
          });
        }
      } catch (err) {
        try { console.warn("[Loop Deep Links] appUrlOpen listener skipped", err); } catch (_) {}
      }
    };

    setupDeepLinks();

    return () => {
      cancelled = true;
      try {
        if (listenerHandle && typeof listenerHandle.remove === "function") listenerHandle.remove();
      } catch (_) {}
    };
  }, [openLoopDeepLink]);

  // Auth & user
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState({ nombre: "", email: "" });
  const [globalCalendarSelectorOpen, setGlobalCalendarSelectorOpen] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authWarning, setAuthWarning] = useState("");
  const [versionGateDecision, setVersionGateDecision] = useState(() => ({
    checked: !isLoopNativeRuntime(),
    status: isLoopNativeRuntime() ? "checking" : "ready",
    reason: isLoopNativeRuntime() ? "initial" : "web",
  }));
  const [softUpdateDismissed, setSoftUpdateDismissed] = useState(false);

  // iOS manual-auth is outside React/Firebase onAuthStateChanged.
  // Keep a React snapshot so Header/MainMenu update immediately on login/logout
  // instead of waiting for a Firestore profile fetch or app reopen.
  const [manualAuthSnapshot, setManualAuthSnapshot] = useState(() => {
    try {
      if (!isIosCapacitor()) return null;
      const ma = getManualAuth();
      return ma && isManualAuthLikelyValid(ma) && ma.uid ? ma : null;
    } catch (_) {
      return null;
    }
  });

  // App state
  const [businessTypes, setBusinessTypes] = useState(defaultBusinessTypes);
  const [businessType, setBusinessType] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [businessName, setBusinessName] = useState(null);
  const [businessList, setBusinessList] = useState([]);
  const [favorites, setFavorites] = useState(new Set());

  // NEW: header location selection
  const [searchLocation, setSearchLocation] = useState(() => {
    try {
      if (typeof window === "undefined") return null;
      return readSearchLocation();
    } catch {
      return null;
    }
  });

  // Startup-safe mode:
  // avoid eager background hooks here to reduce Android blank-screen risk.
  const calendarPendingTotal = 0;
  const calendarPendingByBusiness = {};
  const calendarPendingLoading = false;

  const fetchCategories = useCallback(async () => {
    try {
      if (isIosCapacitor()) {
        const list = await fsClient.listCollectionByPath("serviceCategories", {}, { db });
        const clean = Array.isArray(list) ? list.map((d) => ({ id: d.id, ...(d || {}) })) : [];
        if (clean.length > 0) setBusinessTypes(clean);
        return;
      }

      const colRef = collection(db, "serviceCategories");
      const snap = await getDocs(colRef);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (list.length > 0) setBusinessTypes(list);
    } catch (err) {
      console.warn("fetchCategories failed, keeping defaults", err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchAllBusinesses = useCallback(async () => {
    try {
      const rows = await listMarketBusinesses(db);
      setBusinessList(rows.filter(isPublicBusiness));
    } catch (err) {
      console.error("fetchAllBusinesses error", err);
      setBusinessList([]);
    }
  }, []);

  useEffect(() => {
    fetchAllBusinesses();
  }, [fetchAllBusinesses]);

  const applyImmediateManualAuthSession = useCallback(async (reason = "manual-auth-event") => {
    if (!isIosCapacitor()) return false;

    const session = getEffectiveManualAuthSession();
    if (!session.loggedIn || !session.user) {
      setManualAuthSnapshot(null);
      setUser(null);
      setUserInfo({ nombre: "", email: "" });
      setFavorites(new Set());
      setBusinessId(null);
      setBusinessName(null);
      setBusinessType(null);
      setAuthReady(true);
      setAuthWarning("");
      return false;
    }

    const ma = session.manualAuth || getManualAuth();
    const uid = session.user.uid || ma?.uid || null;
    const email = session.user.email || ma?.email || "";
    const displayName = session.user.displayName || ma?.displayName || ma?.name || "";

    // Critical iOS behavior: update React/UI immediately, before Firestore/native auth finishes.
    setManualAuthSnapshot(ma || null);
    setUser(null);
    setUserInfo({ nombre: displayName || "", email: email || "" });
    setAuthReady(true);
    setAuthWarning("");

    try {
      if (typeof window !== "undefined" && window.__LOOP_AUTH_DEBUG__) {
        console.log("[Loop Auth] immediate manual session", { reason, uid, email, source: session.source });
      }
    } catch (_) {}

    // Non-blocking profile refresh. Header already shows logged-in state above.
    if (uid) {
      try {
        const profile = await fsClient.getDocByPath(`users/${uid}`, { db });
        const latest = getEffectiveManualAuthSession();
        if (!latest.loggedIn || String(latest.user?.uid || "") !== String(uid || "")) return true;

        const nombre = profile?.nombre || profile?.displayName || profile?.name || displayName || "";
        const profileEmail = profile?.email || email || "";
        const favs = Array.isArray(profile?.favorites) ? profile.favorites : [];
        setUserInfo({ nombre, email: profileEmail });
        setFavorites(new Set(favs));
      } catch (_) {
        // Keep the immediate shell. Network/profile failures must not make login look failed.
      }
    }

    return true;
  }, [db]);

  // iOS hot-login listener. This must be outside the bootstrap effect because the
  // iOS branch returns early there and would never attach these listeners.
  useEffect(() => {
    if (!isIosCapacitor()) return undefined;

    const handler = (event) => {
      applyImmediateManualAuthSession(event?.type || "manual-auth-event");
    };

    try {
      window.addEventListener("manual-auth-changed", handler);
      window.addEventListener("manual-auth-detected", handler);
      window.addEventListener("loop-auth-login-success", handler);
      window.addEventListener("manual-auth-cleared", handler);
      window.addEventListener("loop-auth-cleared", handler);
    } catch (_) {}

    // One immediate pass helps when the route changes faster than the first event listener tick.
    handler({ type: "manual-auth-listener-mounted" });

    return () => {
      try {
        window.removeEventListener("manual-auth-changed", handler);
        window.removeEventListener("manual-auth-detected", handler);
        window.removeEventListener("loop-auth-login-success", handler);
        window.removeEventListener("manual-auth-cleared", handler);
        window.removeEventListener("loop-auth-cleared", handler);
      } catch (_) {}
    };
  }, [applyImmediateManualAuthSession]);

  // ------------------------------------------------------------
  // AUTH: Web SDK + iOS manual-auth fallback (REST)
  // ------------------------------------------------------------
  useEffect(() => {
    let isMounted = true;
    let authResolved = false;
    let profileGeneration = 0;

    async function applyEffectiveSessionFromManualAuth() {
      try {
        let ma = getManualAuth();
        if (!ma || !isManualAuthLikelyValid(ma) || !ma.uid) {
          const refreshed = await refreshNativeIosSessionIfPossible();
          ma = refreshed || getManualAuth();
        }

        if (!ma || !isManualAuthLikelyValid(ma) || !ma.uid) {
          // Preserve the native restore marker on temporary refresh/network failures.
          setManualAuthSnapshot(null);
          setUser(null);
          setUserInfo({ nombre: "", email: "" });
          setFavorites(new Set());
          return;
        }

        // Show the logged-in UI immediately. Firestore profile/favorites can refresh
        // in the background, but the Header should not wait for that roundtrip.
        if (isMounted) {
          setManualAuthSnapshot(ma);
          setUser(null);
          setUserInfo({
            nombre: ma.displayName || ma.name || "",
            email: ma.email || "",
          });
          setAuthReady(true);
          setAuthWarning("");
        }

        // Load user profile and favorites from Firestore via REST (iOS-safe)
        try {
          const u = await fsClient.getDocByPath(`users/${ma.uid}`, { db });
          const nombre = (u && (u.nombre || u.displayName || u.name)) ? (u.nombre || u.displayName || u.name) : "";
          const email = (u && u.email) ? u.email : (ma.email || "");
          const favs = Array.isArray(u?.favorites) ? u.favorites : [];
          if (!isMounted || getManualAuth()?.uid !== ma.uid) return;

          // Keep user=null (web auth not trusted on iOS); UI uses userInfo + manual-auth.
          setManualAuthSnapshot(ma);
          setUser(null);
          setUserInfo({ nombre: nombre || "", email: email || "" });
          setFavorites(new Set(favs));
          return;
        } catch (e) {
          if (!isMounted || getManualAuth()?.uid !== ma.uid) return;
          setManualAuthSnapshot(ma);
          setUser(null);
          setUserInfo({ nombre: ma.displayName || ma.name || "", email: ma.email || "" });
          setFavorites(new Set());
        }
      } catch (e) {
        if (!isMounted) return;
        setManualAuthSnapshot(null);
        setUser(null);
        setUserInfo({ nombre: "", email: "" });
        setFavorites(new Set());
      }
    }

    // iOS/Capacitor: do not wait for Firebase Web Auth to resolve.
    // WKWebView can delay onAuthStateChanged for several seconds and that was forcing
    // the app into a visible "modo seguro" banner even though public browsing can work.
    // Use the native/manual session immediately, then let public routes continue.
    if (isIosCapacitor()) {
      let iosDone = false;
      const iosTimer = setTimeout(() => {
        if (!iosDone && isMounted) {
          console.warn("[App] iOS auth bootstrap timed out; continuing public browsing without blocking routes.");
          setManualAuthSnapshot(null);
          setUser(null);
          setUserInfo({ nombre: "", email: "" });
          setFavorites((prev) => prev || new Set());
          setAuthWarning("");
          setAuthReady(true);
        }
      }, 6500);

      applyEffectiveSessionFromManualAuth()
        .catch((err) => {
          try { console.warn("[App] iOS manual session bootstrap failed", err); } catch (_) {}
        })
        .finally(() => {
          iosDone = true;
          clearTimeout(iosTimer);
          if (isMounted) {
            setAuthWarning("");
            setAuthReady(true);
          }
        });

      return () => {
        isMounted = false;
        clearTimeout(iosTimer);
      };
    }

    const fallbackTimer = setTimeout(() => {
      if (!authResolved && isMounted) {
        setAuthWarning("La sesión está tardando en cargar.");
        setAuthReady(true);
      }
    }, 4000);

    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      const generation = ++profileGeneration;
      const isCurrent = () => isMounted && generation === profileGeneration && (auth.currentUser?.uid || null) === (u?.uid || null);
      authResolved = true;
      clearTimeout(fallbackTimer);
      if (!isCurrent()) return;

      // On iOS, prefer manual-auth if present.
      if (isIosCapacitor()) {
        await applyEffectiveSessionFromManualAuth();
        setAuthReady(true);
        setAuthWarning("");
        return;
      }

      // Web/Android default behavior
      try {
        setUser(u);
        setUserInfo({ uid: u?.uid || "", nombre: u?.displayName || "", email: u?.email || "" });
        setFavorites(new Set());
        setAuthReady(true);
        setAuthWarning("");

        if (u && u.email && u.emailVerified === false && isPasswordProviderUser(u)) {
          try {
            window.localStorage.setItem("pending_uid", u.uid);
            window.localStorage.setItem("pending_email", u.email);
          } catch (_) {}

          setUser(null);
          setUserInfo({ nombre: "", email: u.email || "" });
          setFavorites(new Set());

          try {
            const pathname = window.location?.pathname || "";
            if (!isEmailVerificationAllowedPath(pathname)) {
              navigate("/enter-code", {
                replace: true,
                state: {
                  uid: u.uid,
                  email: u.email,
                },
              });
            }
          } catch (_) {}

          return;
        }

        if (u) {
          try {
            const uDoc = doc(db, "users", u.uid);
            const snap = await getDoc(uDoc);

            if (!isCurrent()) return;

            if (snap && snap.exists()) {
              const d = snap.data();
              setUserInfo({
                nombre: d.nombre || "",
                uid: u.uid,
                email: u.email || "",
              });

              const favs = Array.isArray(d.favorites) ? d.favorites : [];
              setFavorites(new Set(favs));
            } else {
              setUserInfo({ nombre: "", email: u.email || "" });
              setFavorites(new Set());
            }
          } catch (e) {
            if (!isCurrent()) return;
            setUserInfo({ nombre: "", email: u.email || "" });
            setFavorites(new Set());
          }
        } else {
          setUserInfo({ nombre: "", email: "" });
          setFavorites(new Set());
        }
      } catch (err) {
        if (!isCurrent()) return;
        setAuthWarning("Hubo un problema cargando la sesión.");
        setAuthReady(true);
      }
    });

    // ALSO listen to manual-auth changes
    const onManualChanged = async (event) => {
      if (!isIosCapacitor()) return;

      // Immediate UI sync. Do not wait for Firestore/native auth.
      try {
        const ma = getManualAuth();
        const valid = Boolean(ma && isManualAuthLikelyValid(ma) && ma.uid);

        if (!valid || event?.type === "manual-auth-cleared" || event?.type === "loop-auth-cleared") {
          setManualAuthSnapshot(null);
          setUser(null);
          setUserInfo({ nombre: "", email: "" });
          setFavorites(new Set());
          setBusinessId(null);
          setBusinessName(null);
          setBusinessType(null);
          setAuthReady(true);
        } else {
          setManualAuthSnapshot(ma);
          setUser(null);
          setUserInfo({
            nombre: ma.displayName || ma.name || "",
            email: ma.email || "",
          });
          setAuthReady(true);
          setAuthWarning("");
        }
      } catch (_) {}

      await applyEffectiveSessionFromManualAuth();
    };
    try {
      window.addEventListener("manual-auth-changed", onManualChanged);
      window.addEventListener("manual-auth-detected", onManualChanged);
      window.addEventListener("manual-auth-cleared", onManualChanged);
      window.addEventListener("loop-auth-cleared", onManualChanged);
    } catch (_) {}

    // Run once on mount (iOS)
    if (isIosCapacitor()) {
      applyEffectiveSessionFromManualAuth().finally(() => {
        if (isMounted) {
          setAuthReady(true);
          setAuthWarning("");
        }
      });
    }

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
      unsubscribe();
      try {
        window.removeEventListener("manual-auth-changed", onManualChanged);
        window.removeEventListener("manual-auth-detected", onManualChanged);
        window.removeEventListener("manual-auth-cleared", onManualChanged);
        window.removeEventListener("loop-auth-cleared", onManualChanged);
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    try {
      const cap = typeof window !== "undefined" && window.Capacitor ? window.Capacitor : null;
      if (cap) {
        const platform = (cap.getPlatform && cap.getPlatform()) || cap.platform || null;
        if (platform && platform !== "web") {
          try {
            document.body.classList.add("is-mobile-app");
            document.body.classList.add(`is-${platform}-app`);
            document.documentElement.classList.add("is-mobile-app");
            document.documentElement.classList.add(`is-${platform}-app`);
            document.documentElement.style.setProperty("--loop-native-platform", `"${platform}"`);
          } catch (e) {}
        }
      }
    } catch (e) {}
    return () => {
      try {
        const cap = typeof window !== "undefined" && window.Capacitor ? window.Capacitor : null;
        const platform = cap ? ((cap.getPlatform && cap.getPlatform()) || cap.platform || null) : null;
        document.body.classList.remove("is-mobile-app");
        document.documentElement.classList.remove("is-mobile-app");
        document.body.classList.remove("loop-android-nav-guard-v34");
        document.documentElement.classList.remove("loop-android-nav-guard-v34");
        document.body.style.removeProperty("--loop-android-system-nav-h");
        document.documentElement.style.removeProperty("--loop-android-system-nav-h");
        document.body.style.removeProperty("--loop-safe-bottom");
        document.documentElement.style.removeProperty("--loop-safe-bottom");
        if (platform && platform !== "web") {
          document.body.classList.remove(`is-${platform}-app`);
          document.documentElement.classList.remove(`is-${platform}-app`);
        }
        document.documentElement.style.removeProperty("--loop-native-platform");
      } catch (e) {}
    };
  }, []);


  // LOOP VERSION GATE / FORCE UPDATE READY V1
  // This prepares future forced updates without blocking now.
  // It only blocks if Firestore appConfig/versionGate is enabled and the current
  // native build is below the configured minimum.
  useEffect(() => {
    let mounted = true;
    let timer = null;

    const run = async () => {
      const platform = normalizeLoopPlatform();

      if (platform !== "android" && platform !== "ios") {
        if (mounted) {
          setVersionGateDecision({ checked: true, status: "ready", platform, reason: "web" });
        }
        return;
      }

      try {
        const decisionPromise = loadLoopVersionGateDecision(db);
        const timeoutPromise = new Promise((resolve) => {
          timer = window.setTimeout(() => {
            resolve({
              checked: true,
              status: "ready",
              platform,
              reason: "timeout-allow",
            });
          }, 3600);
        });

        const decision = await Promise.race([decisionPromise, timeoutPromise]);
        if (!mounted) return;

        setVersionGateDecision(decision || { checked: true, status: "ready", platform, reason: "empty-allow" });

        try {
          window.__LOOP_VERSION_GATE_DECISION__ = decision;
        } catch (_) {}
      } catch (error) {
        if (!mounted) return;
        try { console.warn("[Loop VersionGate] failed", error); } catch (_) {}
        setVersionGateDecision({ checked: true, status: "ready", platform, reason: "effect-error-allow" });
      }
    };

    run();

    return () => {
      mounted = false;
      if (timer) window.clearTimeout(timer);
    };
  }, []);


  // LOOP NATIVE SAFE AREA / HEADER OFFSET V24
  // Native WebViews can report different visible heights than Chrome mobile emulation.
  // This measures the real Loop header after render and exposes it as a CSS variable
  // so every route can reserve the exact top space.
  useEffect(() => {
    if (!isLoopNativeRuntime()) return undefined;

    let cancelled = false;
    let rafId = 0;
    let resizeObserver = null;
    let mutationObserver = null;
    const timers = [];

    const setVar = (name, value) => {
      try {
        document.documentElement.style.setProperty(name, value);
        document.body.style.setProperty(name, value);
      } catch (_) {}
    };

    const measure = () => {
      if (cancelled || typeof document === "undefined") return;

      const header = document.getElementById("app-main-header");
      const rect = header && typeof header.getBoundingClientRect === "function"
        ? header.getBoundingClientRect()
        : null;

      const measured = Math.ceil(rect && rect.height ? rect.height : 64);
      const safeMeasured = Math.max(56, Math.min(120, measured || 64));

      setVar("--loop-native-measured-header-h", `${safeMeasured}px`);
      setVar("--loop-native-header-h", `${safeMeasured}px`);
      setVar("--loop-native-safe-top", "env(safe-area-inset-top, 0px)");
      const platform = window.Capacitor?.getPlatform?.() || window.Capacitor?.platform || "unknown";
      const nativeSafeBottom = platform === "android"
        ? "max(env(safe-area-inset-bottom, 0px), var(--safe-area-inset-bottom, 0px))"
        : "env(safe-area-inset-bottom, 0px)";
      setVar("--loop-native-safe-bottom", nativeSafeBottom);
      setVar("--loop-safe-bottom", nativeSafeBottom);

      try {
        window.__LOOP_NATIVE_HEADER_METRICS__ = {
          measured,
          safeMeasured,
          y: rect ? Math.round(rect.y) : null,
          bottom: rect ? Math.round(rect.bottom) : null,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          visualViewportHeight: window.visualViewport ? window.visualViewport.height : null,
          platform: window.Capacitor?.getPlatform?.() || window.Capacitor?.platform || "unknown",
          at: new Date().toISOString(),
        };
      } catch (_) {}
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(measure);
      [80, 240, 640, 1200].forEach((ms) => timers.push(window.setTimeout(measure, ms)));
    };

    schedule();

    try {
      const header = document.getElementById("app-main-header");
      if (header && typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(header);
      }
    } catch (_) {}

    try {
      if (typeof MutationObserver !== "undefined") {
        mutationObserver = new MutationObserver(schedule);
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      }
    } catch (_) {}

    try {
      window.addEventListener("resize", schedule);
      window.visualViewport?.addEventListener?.("resize", schedule);
      window.visualViewport?.addEventListener?.("scroll", schedule);
      document.addEventListener("visibilitychange", schedule);
    } catch (_) {}

    return () => {
      cancelled = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      try { resizeObserver?.disconnect?.(); } catch (_) {}
      try { mutationObserver?.disconnect?.(); } catch (_) {}
      try {
        window.removeEventListener("resize", schedule);
        window.visualViewport?.removeEventListener?.("resize", schedule);
        window.visualViewport?.removeEventListener?.("scroll", schedule);
        document.removeEventListener("visibilitychange", schedule);
      } catch (_) {}
      try {
        document.documentElement.style.removeProperty("--loop-native-measured-header-h");
        document.body.style.removeProperty("--loop-native-measured-header-h");
        document.documentElement.style.removeProperty("--loop-native-header-h");
        document.body.style.removeProperty("--loop-native-header-h");
      } catch (_) {}
    };
  }, []);



  // ------------------------------------------------------------
  // PUSH NOTIFICATIONS: register this mobile device for owner alerts
  // ------------------------------------------------------------
  useEffect(() => {
    if (!authReady) return;

    let cancelled = false;
    let timer = null;

    const isNativeMobile = () => {
      try {
        const cap = typeof window !== "undefined" ? window.Capacitor : null;
        if (!cap) return false;
        const platform =
          typeof cap.getPlatform === "function"
            ? cap.getPlatform()
            : cap.platform || "web";
        return platform === "ios" || platform === "android";
      } catch (_) {
        return false;
      }
    };

    const run = async () => {
      try {
        // For now, register only native mobile apps. Web push can be added later with VAPID.
        if (!isNativeMobile()) return;

        const result = await registerPushNotifications({ force: false });

        try {
          window.__LOOP_PUSH_REGISTER_RESULT__ = {
            ...(result || {}),
            time: new Date().toISOString(),
          };
        } catch (_) {}

        if (!cancelled) {
          console.log("[Loop Push] register result", result);
        }
      } catch (error) {
        console.warn("[Loop Push] register failed", error);
        try {
          window.__LOOP_PUSH_REGISTER_RESULT__ = {
            ok: false,
            reason: "app-effect-failed",
            error: String(error && error.message ? error.message : error),
            time: new Date().toISOString(),
          };
        } catch (_) {}
      }
    };

    // Small delay lets Firebase/manual auth finish hydrating on mobile.
    timer = setTimeout(run, 900);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [authReady, user?.uid, userInfo?.email]);


  // ------------------------------------------------------------
  // PUSH ROUTING: handle service-worker messages and native events
  // ------------------------------------------------------------
  useEffect(() => {
    const openFromPushData = (rawData) => {
      try {
        const data = getLoopNotificationData(rawData || {});
        const route = buildLoopNotificationRoute(data);
        if (!route) return;
        navigate(route);
      } catch (error) {
        console.warn("[Loop Push] route handling failed", error);
      }
    };

    const handleWindowPushOpen = (event) => {
      const detail = event && event.detail ? event.detail : {};
      if (detail.route) {
        navigate(detail.route);
        return;
      }
      openFromPushData(detail.data || detail);
    };

    const handleServiceWorkerMessage = (event) => {
      const message = event && event.data ? event.data : {};
      if (!message) return;
      if (message.action === "loopPushOpen" || message.action === "openNotification" || message.action === "openRating") {
        if (message.route) {
          navigate(message.route);
          return;
        }
        openFromPushData(message.data || message);
      }
    };

    window.addEventListener("loop:push-open", handleWindowPushOpen);
    if (navigator && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    }

    return () => {
      window.removeEventListener("loop:push-open", handleWindowPushOpen);
      if (navigator && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      }
    };
  }, [navigate]);


  useEffect(() => {
    if (!isIosCapacitor()) return;

    let mounted = true;

    const revalidateManualSession = async () => {
      try {
        let status = getManualAuthStatus();

        if (!status.exists || !status.valid) {
          const refreshed = await refreshNativeIosSessionIfPossible();
          status = getManualAuthStatus();
          if (!refreshed && (!status.exists || !status.valid)) {
            clearManualAuth();
            if (!mounted) return;
            setManualAuthSnapshot(null);
            setUser(null);
            setUserInfo({ nombre: "", email: "" });
            setFavorites(new Set());
            return;
          }
        }

        const ma = getManualAuth();
        if (!ma || !ma.uid) return;

        try {
          const u = await fsClient.getDocByPath(`users/${ma.uid}`, { db });
          if (!mounted) return;
          const nombre = (u && (u.nombre || u.displayName || u.name)) ? (u.nombre || u.displayName || u.name) : "";
          const email = (u && u.email) ? u.email : (ma.email || "");
          const favs = Array.isArray(u?.favorites) ? u.favorites : [];
          setUser(null);
          setUserInfo({ nombre: nombre || "", email: email || "" });
          setFavorites(new Set(favs));
        } catch (_) {
          if (!mounted) return;
          setManualAuthSnapshot(ma);
          setUser(null);
          setUserInfo({ nombre: ma.displayName || ma.name || "", email: ma.email || "" });
          setFavorites(new Set());
        }
      } catch (_) {}
    };

    const onVisible = () => {
      try {
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      } catch (_) {}
      revalidateManualSession();
    };

    const onFocus = () => revalidateManualSession();
    const onPageshow = () => revalidateManualSession();

    revalidateManualSession();

    try {
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("focus", onFocus);
      window.addEventListener("pageshow", onPageshow);
    } catch (_) {}

    return () => {
      mounted = false;
      try {
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("pageshow", onPageshow);
      } catch (_) {}
    };
  }, []);

  const startupSurfaceReady =
    versionGateDecision?.status === "required" ||
    (versionGateDecision?.checked === true && authReady === true);

  useEffect(() => {
    if (!startupSurfaceReady) return undefined;

    let raf1 = 0;
    let raf2 = 0;
    let timer = null;
    let cancelled = false;

    const hideStartupSurface = () => {
      if (cancelled) return;

      try {
        const sharedHide =
          typeof window !== "undefined"
            ? window.__LOOP_HIDE_STARTUP_SPLASH__
            : null;

        if (typeof sharedHide === "function") {
          sharedHide();
          return;
        }

        const splash =
          typeof document !== "undefined"
            ? document.getElementById("app-splash-screen")
            : null;

        if (!splash) return;
        splash.classList.add("app-splash-hidden");
        setTimeout(() => {
          try { splash.remove(); } catch (_) {}
        }, 280);
      } catch (_) {}
    };

    try {
      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        raf1 = window.requestAnimationFrame(() => {
          raf2 = window.requestAnimationFrame(hideStartupSurface);
        });
      } else {
        timer = setTimeout(hideStartupSurface, 0);
      }
    } catch (_) {
      timer = setTimeout(hideStartupSurface, 0);
    }

    return () => {
      cancelled = true;
      try {
        if (raf1) window.cancelAnimationFrame(raf1);
        if (raf2) window.cancelAnimationFrame(raf2);
        if (timer) clearTimeout(timer);
      } catch (_) {}
    };
  }, [startupSurfaceReady]);

  const handleHomeClick = useCallback(async () => {
    setBusinessId(null);
    setBusinessType(null);
    setBusinessName(null);

    try {
      await fetchAllBusinesses();
    } catch (_) {}
    navigate("/");
  }, [fetchAllBusinesses, navigate]);

  const openCalendarRoute = useCallback((bizId = null) => {
    const ma = isIosCapacitor() ? getManualAuth() : null;
    const hasEffectiveUser = Boolean(user) || Boolean(ma && isManualAuthLikelyValid(ma)) || isManualAuthUsableNow();
    if (!hasEffectiveUser) {
      try {
        sessionStorage.setItem("intent_open_calendar_after_login", JSON.stringify({ bizId: bizId || null, source: "global-calendar-selector" }));
      } catch (e) {}
      navigate("/login");
      return;
    }

    // LOOP GLOBAL CALENDAR SELECTOR â€” open as a top-level modal from any route.
    // This makes the Header calendar button work from MyBookings, business settings,
    // GeneralSection, OwnerBusinessesPage, mobile/native shells, etc.
    setGlobalCalendarSelectorOpen(true);
  }, [navigate, user]);

  const handleGlobalCalendarSelect = useCallback((item) => {
    try {
      const bizId = item?.businessId || item?.id || item?.uid || item?.business?.id || null;
      if (!bizId) return;

      const safeBusinessName = pickLoopBusinessDisplayName(item, bizId);

      setGlobalCalendarSelectorOpen(false);
      setBusinessId(bizId);
      setBusinessName(safeBusinessName || null);

      const calendarState = {
        businessId: bizId,
        businessName: safeBusinessName || "Cargando negocio...",
        role: item?.role || "owner",
        employeeId: item?.employeeId || null,
        employeeEmail: item?.employeeEmail || null,
        skipResolve: true,
        fromGlobalCalendarSelector: true,
      };

      if (item?.action === "analytics") {
        navigate(`/business/${encodeURIComponent(bizId)}/analytics`, { state: calendarState });
        return;
      }

      if (item?.role === "worker" && item?.employeeId) {
        navigate(`/business/${encodeURIComponent(bizId)}/calendar?worker=${encodeURIComponent(item.employeeId)}`, { state: calendarState });
        return;
      }

      if (item?.role === "worker" && item?.employeeEmail) {
        navigate(`/business/${encodeURIComponent(bizId)}/calendar?workerEmail=${encodeURIComponent(item.employeeEmail)}`, { state: calendarState });
        return;
      }

      navigate(`/business/${encodeURIComponent(bizId)}/calendar`, { state: calendarState });
    } catch (err) {
      console.warn("[App] global calendar select failed", err);
    }
  }, [navigate]);

  useEffect(() => {
    const onOpenGlobalCalendarSelector = (event) => {
      try { if (event && typeof event.preventDefault === "function") event.preventDefault(); } catch (_) {}
      const bizId = event?.detail?.bizId || event?.detail?.businessId || null;
      openCalendarRoute(bizId);
    };

    try {
      window.addEventListener("loop:open-calendar-selector", onOpenGlobalCalendarSelector);
      window.__loopOpenCalendarSelector = (detail = {}) => {
        try {
          window.dispatchEvent(new CustomEvent("loop:open-calendar-selector", {
            detail,
            cancelable: true,
          }));
        } catch (_) {
          openCalendarRoute(detail?.bizId || detail?.businessId || null);
        }
      };
    } catch (_) {}

    return () => {
      try { window.removeEventListener("loop:open-calendar-selector", onOpenGlobalCalendarSelector); } catch (_) {}
      try { if (window.__loopOpenCalendarSelector) delete window.__loopOpenCalendarSelector; } catch (_) {}
    };
  }, [openCalendarRoute]);

  const handleSearchNearbyLocation = useCallback((locSuggestionOrNull) => {
    if (!locSuggestionOrNull) {
      setSearchLocation(null);
      writeSearchLocation(null);
      return;
    }
    const safe = {
      id: locSuggestionOrNull.id || null,
      label: locSuggestionOrNull.label || "",
      sublabel: locSuggestionOrNull.sublabel || "",
      tokens: locSuggestionOrNull.tokens || "",
      ts: Date.now(),
    };
    setSearchLocation(safe);
    writeSearchLocation(safe);
  }, []);

  const handleSelectService = (svc) => {
    if (!svc) return;

    const candidates = [];

    if (svc && typeof svc === "object") {
      [
        svc.id,
        svc.name,
        svc.title,
        svc.label,
        svc.key,
        svc.slug,
        svc.value,
        svc.serviceKey,
        svc.businessType,
        svc.category,
      ].forEach((v) => {
        if (v != null) candidates.push(String(v));
      });
    } else {
      candidates.push(String(svc));
    }

    const normalizedCandidates = candidates.map((value) =>
      String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
    );

    const isEventsCategory = normalizedCandidates.some((normalized) =>
      normalized === "eventos" ||
      normalized === "events" ||
      normalized === "evento" ||
      normalized.includes("evento")
    );

    if (isEventsCategory) {
      navigate("/events");
      return;
    }

    const candidate = candidates.find((x) => String(x || "").trim()) || "";
    const rawKey = String(candidate).trim();
    if (!rawKey) return;

    try {
      navigate(`/services/search?q=${encodeURIComponent(rawKey)}`);
    } catch (e) {
      try {
        navigate(`/services/search?q=${rawKey}`);
      } catch (_) {
        window.location.href = `/services/search?q=${rawKey}`;
      }
    }
  };

  const handleSelectBusiness = (biz) => {
    setBusinessId(biz.id);
    setBusinessName(biz.name);
    navigate(`/business/${encodeURIComponent(getBusinessRouteParam(biz) || biz.id)}`);
  };

  const handleOpenBooking = (biz) => {
    setBusinessId(biz.id);
    setBusinessName(biz.name);
    navigate("/booking", { state: { businessId: biz.id, businessName: biz.name } });
  };

  const favoriteSession = isIosCapacitor() ? getManualAuth() : null;
  const handleToggleFavorite = useFavoriteActions({
    uid: user?.uid || (favoriteSession && isManualAuthLikelyValid(favoriteSession) ? favoriteSession.uid : null),
    favorites, onChange: setFavorites, db,
    onLogin: () => navigate("/login?from=" + encodeURIComponent(location.pathname + location.search)),
    onError: () => alert("No se pudo guardar el favorito. Intenta nuevamente."),
  });

  const handleCreateBusinessClick = () => navigate("/new-business");

  // IMPORTANT: Logout must clear manual-auth to avoid "ghost login" in iOS
  const handleLogout = useCallback(async () => {
    try {await Promise.race([unregisterPushNotifications(), new Promise(resolve => setTimeout(resolve, 3000))]);} catch (error) {console.warn("Push cleanup failed",error);}

    // Clear every Loop/Firebase/Auth cache FIRST so UI updates even if signOut hangs.
    clearLoopAuthState();

    // Immediate visual logout. Do this before any await so iOS cannot feel stuck.
    setManualAuthSnapshot(null);
    setUser(null);
    setUserInfo({ nombre: "", email: "" });
    setFavorites(new Set());
    setBusinessId(null);
    setBusinessName(null);
    setBusinessType(null);
    setAuthReady(true);
    setAuthWarning("");

    try {
      navigate("/login", { replace: true });
    } catch (_) {}

    // Native/Firebase sign-out can be slow on iOS. Run it after UI has already reset.
    try {
      if (isIosCapacitor() && isFirebaseNativeAvailable()) {
        try { await nativeFirebaseSignOut(); } catch (_) {}
      }
      await signOut(auth);
    } catch (e) {
      console.warn(e);
    } finally {
      clearLoopAuthState();
      setManualAuthSnapshot(null);
      setUser(null);
      setUserInfo({ nombre: "", email: "" });
      setFavorites(new Set());
    }
  }, [navigate]);


  const effectiveSession = useMemo(() => {
    return buildEffectiveSession({
      firebaseUser: user,
      userInfo,
      isIosRuntime: isIosCapacitor(),
    });
  }, [user, userInfo, manualAuthSnapshot]);

  const appSessionResolvedLoggedIn = isEffectiveSessionLoggedIn(effectiveSession);
  const authSessionKey = getEffectiveSessionKey(effectiveSession);

  const previousAuthSessionKeyRef = useRef(authSessionKey);

  useEffect(() => {
    const previous = previousAuthSessionKeyRef.current;
    if (previous === authSessionKey) return;

    previousAuthSessionKeyRef.current = authSessionKey;

    // Hard isolation between accounts: never let user A's selected business,
    // favorites, profile shell or calendar state survive into user B/guest.
    setBusinessId(null);
    setBusinessName(null);
    setBusinessType(null);
    setFavorites(new Set());

    if (authSessionKey === "guest") {
      setUserInfo({ nombre: "", email: "" });
    }

    try {
      window.dispatchEvent(new CustomEvent("loop-auth-session-changed", {
        detail: { previous, next: authSessionKey },
      }));
    } catch (_) {}
  }, [authSessionKey]);

  const headerUserInfo = effectiveSession.headerUserInfo || { nombre: "", email: "" };

  function renderMainMenu({ headerOnly = false } = {}) {
    return (
      <>
        <AuthRecoveryNotice message={authWarning} />

        <MainMenu
          db={db}
          user={user || (favoriteSession && isManualAuthLikelyValid(favoriteSession) ? favoriteSession : null)}
          userEmail={headerUserInfo.email}
          userName={headerUserInfo.nombre}
          isLoggedIn={appSessionResolvedLoggedIn}
          authReady={authReady}
          onLogout={handleLogout}
          onAdminManage={() => navigate("/owner-businesses")}
          onHomeClick={handleHomeClick}
          onShowUserProfile={() => navigate("/profile")}
          onShowPendingsPanel={() => navigate("/calificar-servicios")}
          onShowMyBookings={() => navigate("/my-bookings")}
          onShowMyLoyalty={() => navigate("/my-benefits")}
          onCreateBusiness={handleCreateBusinessClick}
          onSearch={(term) => {
            const clean = String(term || "").trim();
            if (!clean) {
              setBusinessType(null);
              navigate("/");
              return;
            }

            setBusinessType(clean);

            const normalized = clean
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase();

            if (normalized.includes("evento")) {
              navigate("/events");
              return;
            }

            navigate(`/services/search?q=${encodeURIComponent(clean)}`);
          }}
          onSearchNearby={handleSearchNearbyLocation}
          businessTypes={businessTypes}
          showHeaderOnly={headerOnly}
          recommendedBusinesses={businessList.filter(isPublicBusiness)}
          showRecommended={!businessType && !businessId}
          onSelectBusiness={handleSelectBusiness}
          onOpenBooking={handleOpenBooking}
          onSelectService={handleSelectService}
          favoritesBusinesses={businessList.filter((biz) => isPublicBusiness(biz) && favorites.has(biz.id))}
          onToggleFavorite={handleToggleFavorite}
          favoritesSet={favorites}
          onOpenBusinessCalendar={(b) => {
            setBusinessId(b.id);
            navigate(businessAdminPath(b, "calendar"));
          }}
          onOpenCalendar={(bizId) => openCalendarRoute(bizId)}
          calendarPendingCount={calendarPendingTotal}
          calendarPendingLoading={calendarPendingLoading}
          calendarPendingByBusiness={calendarPendingByBusiness}
          searchLocation={searchLocation}
        />
      </>
    );
  }

  if (versionGateDecision?.status === "required") {
    return <LoopUpdateRequiredScreen decision={versionGateDecision} />;
  }

  if (!versionGateDecision?.checked && isLoopNativeRuntime()) {
    return (
      <LoopStartupSurface ariaLabel="Verificando versión de Loop" />
    );
  }

  if (!authReady) {
    return (
      <LoopStartupSurface ariaLabel="Inicializando Loop" />
    );
  }

  return (
    <>
      <BookingNotificationToasts key={`toasts-${authSessionKey}`} user={user} db={db} />
      <AuthDebugPanel auth={auth} user={user} userInfo={userInfo} authSessionKey={authSessionKey} />
      {!softUpdateDismissed && versionGateDecision?.status === "optional" ? (
        <LoopSoftUpdateNotice
          decision={versionGateDecision}
          onDismiss={() => setSoftUpdateDismissed(true)}
        />
      ) : null}
      <WorkerBusinessesModal
        open={globalCalendarSelectorOpen}
        onClose={() => setGlobalCalendarSelectorOpen(false)}
        onSelect={handleGlobalCalendarSelect}
      />
      <LegalUpdateModal
        db={db}
        user={user}
        userInfo={userInfo}
        isLoggedIn={appSessionResolvedLoggedIn}
      />
      <LoopAuthenticatedProfileGateR31
        isLoggedIn={appSessionResolvedLoggedIn}
        user={user}
        authSessionKey={authSessionKey}
      />
      <Routes key={authSessionKey}>
      {/* LOOP D1.4.1 â€” Loyalty QR route guard: keep this route at the top so QR links never fall into the catch-all. */}
      <Route
        path="/loyalty/checkin/:token"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <LoyaltyCheckinPage />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/loyalty/checkin/*"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <LoyaltyCheckinPage />
            </div>
          </ProtectedRoute>
        }
      />
        <Route
          path="/login"
          element={
            <Login
              authReady={authReady}
              isLoggedIn={appSessionResolvedLoggedIn}
            />
          }
        />

      <Route
        path="/forgot-password"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <ForgotPassword />
          </div>
        }
      />
      <Route
        path="/reset-password"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <ResetPassword />
          </div>
        }
      />

      <Route
        path="/signup"
        element={<Navigate to="/register" replace />}
      />
      <Route
        path="/register"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <Register />
          </div>
        }
      />
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute requireCompleteProfile={false}>
            <CompleteProfileOnboarding />
          </ProtectedRoute>
        }
      />

      <Route
        path="/verify-email"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <VerifyEmailPage db={db} />
          </div>
        }
      />
      <Route
        path="/enter-code"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <EnterCode />
          </div>
        }
      />

      <Route
        path="/worker-invite"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <WorkerInvitePage />
          </div>
        }
      />



      <Route
        path="/support"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <SupportPage />
          </div>
        }
      />

      <Route
        path="/soporte"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <SupportPage />
          </div>
        }
      />

      <Route
        path="/privacy"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PrivacyPage />
          </div>
        }
      />

      <Route
        path="/privacy-policy"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PrivacyPage />
          </div>
        }
      />

      <Route
        path="/privacidad"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PrivacyPage />
          </div>
        }
      />

      <Route
        path="/politica"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PrivacyPage />
          </div>
        }
      />

      <Route
        path="/politica-de-privacidad"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PrivacyPage />
          </div>
        }
      />

      <Route
        path="/terms"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <TermsPage />
          </div>
        }
      />

      <Route
        path="/terminos"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <TermsPage />
          </div>
        }
      />

      <Route
        path="/terminos-y-condiciones"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <TermsPage />
          </div>
        }
      />

      <Route
        path="/payments"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PaymentsPolicyPage />
          </div>
        }
      />

      <Route
        path="/payments-policy"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PaymentsPolicyPage />
          </div>
        }
      />

      <Route
        path="/pagos"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PaymentsPolicyPage />
          </div>
        }
      />

      <Route
        path="/politica-de-pagos"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PaymentsPolicyPage />
          </div>
        }
      />

      <Route
        path="/pagos-cancelaciones-reembolsos"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <PaymentsPolicyPage />
          </div>
        }
      />

      {/* Public early-access lead form. No login required. */}
      <Route
        path="/early-access"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <EarlyAccessLeadPage db={db} />
          </div>
        }
      />

      {/* Private CRM for early-access leads. UI is limited to the allowed owner email. */}
      <Route
        path="/admin/early-access"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <EarlyAccessAdminPage db={db} auth={auth} />
          </div>
        }
      />

      {/* Private owner console for platform operations. */}
      <Route
        path="/owner-console"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <OwnerConsolePage db={db} auth={auth} user={effectiveSession.user} />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/business/:businessId/edit"
        element={
          <ProtectedRoute>
            <BusinessEditRoute
              renderMainMenu={renderMainMenu}
              db={db}
              user={effectiveSession.user}
              businessTypes={businessTypes}
              fetchAllBusinesses={fetchAllBusinesses}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/business/:businessId"
        element={
          <div>
            {renderMainMenu({})}
            <BusinessPage db={db} />
          </div>
        }
      />

      <Route
        path="/business/:businessId/customers"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <CustomersPage />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/loyalty/checkin/:token"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <LoyaltyCheckinPage />
            </div>
          </ProtectedRoute>
        }
      />



      <Route
        path="/business/:businessId/analytics"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <BusinessAnalyticsPage db={db} />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/business/:businessId/calendar"
        element={
          <ProtectedRoute>
            <CalendarSafeRoute renderMainMenu={renderMainMenu} authSessionKey={authSessionKey} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/events"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <EventsHomePage />
          </div>
        }
      />
      <Route
        path="/events/:categoryKey"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <EventCategoryPage />
          </div>
        }
      />
      <Route
        path="/services/:serviceKey"
        element={<ServiceResultsSafeRoute renderMainMenu={renderMainMenu} />}
      />
      <Route
        path="/admin-calendar/:businessId"
        element={
          <ProtectedRoute>
            <CalendarSafeRoute renderMainMenu={renderMainMenu} businessIdFallback={businessId} authSessionKey={authSessionKey} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-calendar"
        element={
          <ProtectedRoute>
            <CalendarSafeRoute renderMainMenu={renderMainMenu} businessIdFallback={businessId} authSessionKey={authSessionKey} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/pending-reservations"
        element={
          <ProtectedRoute>
            <PendingReservationsRoute renderMainMenu={renderMainMenu} db={db} user={user} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/location-results"
        element={
          <div>
            {renderMainMenu({ headerOnly: true })}
            <LocationResultsPage db={db} />
          </div>
        }
      />

      <Route
        path="/new-business"
        element={
          <div>
            {renderMainMenu({})}
            <NewBusinessPage user={effectiveSession.user} db={db} businessTypes={businessTypes} />
          </div>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute>
            <Navigate to="/owner-console" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/debug-calendar"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <MyCalendarDebugger />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booking"
        element={
          <div>
            {renderMainMenu({})}
            <BookingInterface businessId={businessId} businessName={businessName} user={user} />
          </div>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <ProfilePage key={`profile-${authSessionKey}`} db={db} auth={auth} user={user} onClose={() => navigate(-1)} />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/pendings"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <Navigate to="/calificar-servicios" replace />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/owner-businesses"
        element={
          <ProtectedRoute>
            <div className="owner-businesses-route">
              {renderMainMenu({ headerOnly: true })}
              <OwnerPage user={effectiveSession.user} />
            </div>
          </ProtectedRoute>
        }
      />


      <Route
        path="/my-benefits"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <MyBenefitsPage />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-loyalty"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <MyLoyaltyPage />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-memberships"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <MyMembershipsPage />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/membership-checkout/:businessId"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <MembershipSubscriptionCheckoutPage />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/membership-checkout/:businessId/:planId"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <MembershipSubscriptionCheckoutPage />
            </div>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-bookings"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <div className="max-w-6xl mx-auto px-6 py-6">
                <MyBookings businessId={businessId || null} onClose={() => navigate("/")} />
              </div>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calificar-servicios"
        element={
          <ProtectedRoute>
            <div>
              {renderMainMenu({ headerOnly: true })}
              <div className="max-w-6xl mx-auto px-6 py-6">
                <CalificarServicios />
              </div>
            </div>
          </ProtectedRoute>
        }
      />
<Route
        path="/admin-booking-calendar/:businessId"
        element={
          <ProtectedRoute>
            <AdminBookingCalendarRouteWrapper renderMainMenu={renderMainMenu} db={db} user={user} authSessionKey={authSessionKey} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/open-calendar"
        element={
          <OpenCalendarRoute
            renderMainMenu={renderMainMenu}
            db={db}
            setBusinessId={setBusinessId}
            setBusinessName={setBusinessName}
          />
        }
      />

      <Route path="/negocios" element={<div>{renderMainMenu({})}</div>} />
      <Route
        path="/"
        element={
          <div>
            {renderMainMenu({})}
          </div>
        }
      />
<Route
        path="*"
        element={
          <LegacyRatingLinkFallback>
          <div>
            {renderMainMenu({})}
            <div className="max-w-6xl mx-auto mt-6 px-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-slate-700">Página no encontrada</h3>
              </div>
            </div>
          </div>
          </LegacyRatingLinkFallback>
        }
      />
</Routes>
    </>
  );
}
